"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Image from "next/image";
// import { SnappyFixLogo } from "/images/logo";
import { Menu, X } from "lucide-react"; // Professional iconography

export default function ToolTopNav() {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // // Handle scroll effect for glassmorphism refinement
  // useEffect(() => {
  //   const handleScroll = () => setScrolled(window.scrollY > 20);
  //   window.addEventListener("scroll", handleScroll);
  //   return () => window.removeEventListener("scroll", handleScroll);
  // }, []);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };

    // The passive flag tells the browser we won't call preventDefault,
    // drastically improving scroll performance.
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "/" },
    { name: "All Tools", href: "/tools" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 border-b 
      ${
        scrolled
          ? "bg-white/70 backdrop-blur-xl border-slate-200 py-2 shadow-sm"
          : "bg-transparent border-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link
          href="/#hero"
          aria-label="Snappy-Fix Technologies home"
          className="group relative flex items-center gap-2.5 select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c3003a] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded-md"
        >
          {/* Hover glow */}
          <span className="absolute inset-[-8px] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(195,0,58,0.2)_0%,transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

          {/* Image container */}
          <span className="relative z-10 flex items-center justify-center w-9 h-9 sm:w-11 sm:h-11 rounded-[10px] border border-white/10 bg-white/[0.03] overflow-hidden transition-all duration-300 group-hover:border-[#c3003a]/50 group-hover:shadow-[0_0_0_3px_rgba(195,0,58,0.12)]">
            <Image
              src="/images/logo/snappy-fix-logo.webp"
              alt="Snappy-Fix Technologies"
              width={48}
              height={48}
              priority
              className="w-full h-full object-contain transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:scale-110 group-hover:-rotate-2"
              sizes="(max-width: 640px) 36px, 44px"
            />
          </span>
          {/* Wordmark */}
          <span className="relative z-10 inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-2 backdrop-blur-sm leading-none font-['Gill_Sans',_'Gill_Sans_MT',_Calibri,_'Trebuchet_MS',_sans-serif] shadow-[0_8px_30px_rgba(0,0,0,0.12)] transition-all duration-300 group-hover:bg-white/10 group-hover:border-white/15">
            <span className="text-[1.1rem] sm:text-[1.2rem] font-extrabold tracking-[-0.035em] text-black/60">
              Snappy
            </span>

            <span className="inline-block w-[4px] h-[4px] rounded-full bg-[#fb397d] mx-[3px] mb-[1px] transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:scale-[1.7] group-hover:bg-[#9572e8]" />

            <span className="text-[1.1rem] sm:text-[1.2rem] font-bold tracking-[-0.035em] bg-gradient-to-r from-[#fb397d] to-[#9572e8] bg-clip-text text-transparent transition-[filter] duration-300 group-hover:brightness-125">
              Fix
            </span>
          </span>
          <span className="sr-only">Snappy-Fix Technologies</span>
        </Link>

        {/* Desktop Navigation Links */}
        <div className="hidden md:flex items-center bg-slate-100/50 p-1 rounded-full border border-slate-200/50">
          {navLinks.map((link) => {
            const isActive = pathname === link.href;
            return (
              <Link
                key={link.href}
                href={link.href}
                className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-200
                ${
                  isActive
                    ? "bg-white text-[#5b32b4] shadow-sm ring-1 ring-slate-200"
                    : "text-slate-600 hover:text-[#5b32b4]"
                }`}
              >
                {link.name}
              </Link>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="hidden md:flex items-center gap-4">
          <Link
            href="/login"
            className="text-sm font-bold text-slate-600 hover:text-[#5b32b4] px-4 transition-colors"
          >
            Sign In
          </Link>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="md:hidden p-2 text-slate-600"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 bg-white border-b border-slate-100 p-6 flex flex-col gap-4 md:hidden animate-in slide-in-from-top-5 duration-300">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={() => setIsOpen(false)}
              className={`text-lg font-bold ${pathname === link.href ? "text-[#5b32b4]" : "text-slate-600"}`}
            >
              {link.name}
            </Link>
          ))}
          <hr className="border-slate-100" />
          <Link
            href="/get-started"
            className="w-full bg-[#5b32b4] text-white py-4 rounded-2xl text-center font-bold"
          >
            Get Started
          </Link>
        </div>
      )}
    </nav>
  );
}
